import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin, Typography } from 'antd';
import { SelectProps } from 'antd/es/select';
import { debounce } from 'lodash';
import type { 
    SelectFieldOption, 
    SelectFieldProviderInfo 
} from '@/shared/api/model';
import { useDynamicSelect } from '../hooks/useDynamicSelect';

const { Option } = Select;
const { Text } = Typography;

export interface DynamicSelectProps extends Omit<SelectProps, 'options' | 'loading'> {
    /**
     * The provider name to fetch options from
     */
    providerName: string;
    
    /**
     * Field name for context
     */
    fieldName?: string;
    
    /**
     * Entity type for context
     */
    entityType?: string;
    
    /**
     * Category ID for context
     */
    categoryId?: number;
    
    /**
     * Custom API base URL (defaults to /api/field-configuration)
     */
    apiBaseUrl?: string;
    
    /**
     * Debounce delay for search in milliseconds
     */
    debounceMs?: number;
    
    /**
     * Show option descriptions
     */
    showDescriptions?: boolean;
    
    /**
     * Group options by the group field
     */
    groupOptions?: boolean;
}

const DynamicSelect: React.FC<DynamicSelectProps> = ({
    providerName,
    fieldName,
    entityType,
    categoryId,
    apiBaseUrl = '/api/field-configuration',
    debounceMs = 300,
    showDescriptions = true,
    groupOptions = false,
    placeholder,
    ...selectProps
}) => {
    const [searchValue, setSearchValue] = useState('');
    const { 
        options, 
        providerInfo, 
        loading, 
        error, 
        fetchOptions 
    } = useDynamicSelect({
        providerName,
        fieldName,
        entityType,
        categoryId
    });

    // Debounced search function
    const debouncedFetchOptions = useMemo(
        () => debounce((search: string) => fetchOptions(search), debounceMs),
        [fetchOptions, debounceMs]
    );

    // Handle search
    const handleSearch = (value: string) => {
        setSearchValue(value);
        
        if (!providerInfo?.supportsSearch) {
            return;
        }

        if (value.length >= (providerInfo?.minSearchChars || 0)) {
            debouncedFetchOptions(value);
        } else if (value.length === 0) {
            fetchOptions();
        }
    };

    // Group options if needed
    const groupedOptions = useMemo(() => {
        if (!groupOptions) return options;

        const groups: Record<string, SelectFieldOption[]> = {};
        const ungrouped: SelectFieldOption[] = [];

        options.forEach(option => {
            if (option.group) {
                if (!groups[option.group]) {
                    groups[option.group] = [];
                }
                groups[option.group].push(option);
            } else {
                ungrouped.push(option);
            }
        });

        return { groups, ungrouped };
    }, [options, groupOptions]);

    // Render option with description (for dropdown list)
    const renderOption = (option: SelectFieldOption) => (
        <Option 
            key={option.value} 
            value={option.value} 
            disabled={option.disabled}
            title={option.description}
            label={option.label} // This ensures only the label shows in the selected value
        >
            <div>
                <div>{option.label}</div>
                {showDescriptions && option.description && (
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                        {option.description}
                    </Text>
                )}
            </div>
        </Option>
    );

    // Generate placeholder text
    const getPlaceholder = () => {
        if (placeholder) return placeholder;
        if (error) return 'Error loading options';
        if (loading) return 'Loading options...';
        if ((providerInfo?.minSearchChars ?? 0) > 0) {
            return `Type at least ${providerInfo?.minSearchChars} characters to search`;
        }
        return `Select ${providerInfo?.description || 'an option'}`;
    };

    // Render options
    const renderOptions = () => {
        if (error) {
            return (
                <Option disabled value="">
                    <Text type="danger">{error}</Text>
                </Option>
            );
        }

        if (groupOptions && typeof groupedOptions === 'object' && 'groups' in groupedOptions) {
            const { groups, ungrouped } = groupedOptions;
            
            return (
                <>
                    {ungrouped.map(renderOption)}
                    {Object.entries(groups).map(([groupName, groupOptions]) => (
                        <Select.OptGroup key={groupName} label={groupName}>
                            {groupOptions.map(renderOption)}
                        </Select.OptGroup>
                    ))}
                </>
            );
        }

        return options.map(renderOption);
    };

    const supportsSearch = providerInfo?.supportsSearch || false;

    // Load initial options on mount
    useEffect(() => {
        if (!options || options.length === 0) {
            fetchOptions();
        }
    }, [providerName]); // Only run when provider changes

    return (
        <Select
            {...selectProps}
            loading={loading}
            placeholder={getPlaceholder()}
            showSearch={supportsSearch}
            onSearch={supportsSearch ? handleSearch : undefined}
            searchValue={supportsSearch ? searchValue : undefined}
            filterOption={false} // We handle filtering on the backend
            notFoundContent={loading ? <Spin size="small" /> : 'No options found'}
            optionLabelProp="label" // Use the label prop for displaying selected value
        >
            {renderOptions()}
        </Select>
    );
};

export default DynamicSelect;
